import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone } from "lucide-react";

const PHONE = "617-717-9852";
const PHONE_HREF = "tel:+16177179852";

const links = [
  { label: "Spaces", href: "#spaces" },
  { label: "How it works", href: "#how" },
  { label: "Pricing", href: "#pricing" },
  { label: "Areas", href: "#areas" },
];

export default function Navbar({ onStart }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-bar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div
          className={`flex items-center justify-between rounded-full px-5 sm:px-7 py-3 transition-all duration-500 ${
            scrolled
              ? "bg-canvas/80 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(23,23,23,0.18)]"
              : "bg-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid="nav-logo"
            className="font-logo font-semibold text-[1.7rem] sm:text-[2.05rem] leading-none tracking-tight text-violet lowercase"
          >
            milla
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[0.95rem] font-medium text-charcoal/70 hover:text-violet transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_HREF}
              data-testid="nav-phone"
              className="hidden lg:inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-charcoal/75 transition-colors duration-300 hover:text-violet"
            >
              <Phone size={15} /> {PHONE}
            </a>
            <button
              onClick={onStart}
              data-testid="nav-start-cta"
              className="hidden sm:inline-flex items-center rounded-full bg-violet px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-violet-deep hover:shadow-[0_10px_30px_-8px_rgba(76,0,140,0.55)] hover:-translate-y-0.5"
            >
              Get an instant quote
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              className="md:hidden flex h-11 w-11 items-center justify-center rounded-full bg-canvas/80 backdrop-blur"
              aria-label="Menu"
            >
              <div className="space-y-1.5">
                <span className={`block h-0.5 w-5 bg-charcoal transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
                <span className={`block h-0.5 w-5 bg-charcoal transition-opacity ${open ? "opacity-0" : ""}`} />
                <span className={`block h-0.5 w-5 bg-charcoal transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
              </div>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-3 rounded-3xl bg-canvas/95 backdrop-blur-xl p-6 shadow-[0_20px_60px_-20px_rgba(23,23,23,0.25)]"
            >
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block py-3 font-display text-2xl text-charcoal"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onStart();
                }}
                data-testid="nav-mobile-start-cta"
                className="mt-4 w-full rounded-full bg-violet px-6 py-4 text-base font-semibold text-white"
              >
                Get an instant quote
              </button>
              <a
                href={PHONE_HREF}
                data-testid="nav-mobile-phone"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-4 text-base font-semibold text-charcoal ring-1 ring-stone"
              >
                <Phone size={17} /> Call {PHONE}
              </a>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
